<template>
  <RoomMap id="set-tables-room-table" :backgroundMode="roomMapBgMode">
    <RoomTable
      v-for="(table, index) in disabledTables"
      :key="table.id"
      :id="`table-${table.id}`"
      :height="table.h"
      :width="table.w"
      :rotation="table.rotation"
      :shape="table.shape"
      :x="table.x"
      :y="table.y"
      marker="xmark"
      class="table unavailable cursor-not-allowed"
      :class="[`table-unavailable-${index + 1}`, { 'standing-room': table.standing_room }]"
      fillClass="fill-table-disabled"
    />
    <RoomTable
      v-for="table in tablesNotFulfillTableMinimum"
      :key="table.id"
      :id="`table-${table.id}`"
      @click="selectTable(table.id)"
      :height="table.h"
      :width="table.w"
      :rotation="table.rotation"
      :shape="table.shape"
      :x="table.x"
      :y="table.y"
      class="table cursor-pointer notFitMinimum"
      :class="{ 'standing-room': table.standing_room }"
      fill-class="fill-table-notFitMinimum"
    />
    <RoomTable
      v-for="(table, index) in availableTables"
      :key="table.id"
      :id="`table-${table.id}`"
      @click="selectTable(table.id)"
      :height="table.h"
      :width="table.w"
      :rotation="table.rotation"
      :shape="table.shape"
      :x="table.x"
      :y="table.y"
      :marker="
        table.isSelected &&
        selectedTables.length === 1 &&
        reservedGuests === number_of_guests &&
        !table.standing_room
          ? 'checkmark'
          : 'none'
      "
      class="table available cursor-pointer"
      :class="[
        `table-available-${index + 1}`,
        `${table.available_guests}-guests`,
        table.isSelected ? 'selected' : 'unselected',
        { 'standing-room': table.standing_room },
      ]"
      :fillClass="table.isSelected ? 'fill-primary' : 'fill-table-available'"
      :textFillClass="!table.standing_room ? 'fill-button-label' : tableLabelColor"
      strokeClass="!outline-none focus:stroke-body-primary"
      @keydown.enter.space="selectTable(table.id)"
      :ariaLabel="getAriaLabel(table, table.isSelected)"
      :isFocused="focusTableId === `table-${table.id}`"
      focusable
      :truncate="table.standing_room"
      :fontSize="table.standing_room && standingRoomTextSize ? +standingRoomTextSize : 1.3"
    >
      {{ tableName(table) }}
    </RoomTable>
  </RoomMap>
</template>

<script lang="ts">
import { useActiveElement, useCssVar } from '@vueuse/core'
import { orderBy } from 'lodash-es'
import { mapState, mapStores } from 'pinia'
import { computed, defineComponent } from 'vue'

import RoomTable from '@/common/components/molecules/RoomTable.vue'
import RoomMap from '@/common/components/organisms/RoomMap.vue'
import { ROOM_MAP_BG_MODE, RoomMapBgMode } from '@/common/interfaces/RoomMap'
import { EnrichedTable, tablesForSeatedParty } from '@/orders'
import { useCheckoutStore } from '@/stores/checkout'
import { useSettingStore } from '@/stores/setting'
import { byId, sum } from '@/utils/utils'

import { FeatureFlag, PerformanceTableId, PerformanceTableRead } from '@generated/types'

export default defineComponent({
  name: 'SetTablesRoomMap',
  components: { RoomTable, RoomMap },
  setup() {
    const activeElement = useActiveElement()
    const focusTableId = computed<string | undefined>(() => {
      if (!activeElement.value) return
      const group = activeElement.value.closest('g')
      if (!group) return
      return group.id
    })
    const roomMapBgMode = computed<RoomMapBgMode>(
      () => useCssVar('--mode').value || ROOM_MAP_BG_MODE.FORCE_LIGHT,
    )
    const tableLabelColor = computed(() =>
      roomMapBgMode.value === ROOM_MAP_BG_MODE.FORCE_LIGHT ? 'text-white' : 'text-black',
    )
    const standingRoomTextSize = useCssVar('--standing-room-text-size')
    return { focusTableId, roomMapBgMode, tableLabelColor, standingRoomTextSize }
  },
  async mounted() {
    if (!this.checkoutStore.performance?.id) {
      return
    }
    await this.checkoutStore.refreshPerformance()
  },
  methods: {
    async selectTable(tableId: number) {
      const selectedTable = this.tables.find(byId(tableId)) as PerformanceTableRead

      const indexOfSelected = this.selectedTableIds.indexOf(selectedTable.id)
      if (indexOfSelected > -1) {
        // on click, deselect any already selected table
        this.selectedTableIds.splice(indexOfSelected, 1)
        return
      }

      // Either the table is available or it is part of the anonymous reservation
      const availability =
        selectedTable.available || this.reservation?.tables.includes(selectedTable.id)
      if (!availability || selectedTable.minimum_guests > this.number_of_guests) {
        // bail out if the table is not available or the party is too small
        return
      }

      if (selectedTable.maximum_guests >= this.number_of_guests) {
        // quick bail if the table holds the entire party
        this.selectedTableIds = [selectedTable.id]
        return
      }

      const shouldStartOver = (seated: EnrichedTable[]): boolean => {
        return this.enforceTableMinimumEnabled
          ? seated.some(table => table.guests < table.minimum_guests)
          : seated.some(table => table.guests === 0)
      }
      while (this.selectedTableIds.length > 0) {
        // assume we completely fill the just-selected table, seat remaining guests at previously selected tables,
        let seated = tablesForSeatedParty(
          this.performance,
          this.selectedTableIds,
          this.number_of_guests - selectedTable.maximum_guests,
        )
        // remove the earliest selected table if any table is totally empty, then start over
        if (shouldStartOver(seated)) {
          this.selectedTableIds.shift()
        } else {
          // now try the whole party with all the tables
          seated = tablesForSeatedParty(
            this.performance,
            [...this.selectedTableIds, selectedTable.id],
            this.number_of_guests,
          )
          if (shouldStartOver(seated)) {
            this.selectedTableIds.shift()
          } else {
            break
          }
        }
      }

      // we delay actually selecting the table until the very end in order to simplify running the algorithm above
      this.selectedTableIds.push(selectedTable.id)
    },
    getAriaLabel(table: PerformanceTableRead, isSelected: boolean): string {
      const selected = isSelected ? 'Selected' : 'Not Selected'
      return `${selected}, ${
        this.settingStore.tableType(table.type_id).name
      }, ${this.settingStore.currency(table.price_per_person)} per person`
    },
    tableName(table: PerformanceTableRead & { isSelected: boolean }): string {
      if (table.standing_room) return table.name
      else if (table.isSelected)
        return (this.seatedTables.map(t => t.id).indexOf(table.id) + 1).toString()
      return ''
    },
  },
  computed: {
    ...mapStores(useCheckoutStore, useSettingStore),
    ...mapState(useCheckoutStore, ['performance', 'number_of_guests', 'reservation']),
    selectedTableIds: {
      get(): PerformanceTableId[] {
        return this.checkoutStore.tables
      },
      set(value: PerformanceTableId[]) {
        this.checkoutStore.tables = value
      },
    },
    enforceTableMinimumEnabled(): boolean {
      return this.settingStore.flagEnabled(FeatureFlag.ENFORCE_MINIMUMS)
    },
    tables(): PerformanceTableRead[] {
      return (this.performance && this.performance.tables) || []
    },
    tablesNotFulfillTableMinimum(): PerformanceTableRead[] {
      if (
        this.enforceTableMinimumEnabled &&
        this.reservedGuests > 0 &&
        this.reservedGuests < this.number_of_guests
      ) {
        return this.tables.filter(
          t =>
            !this.disabledTables.map(t => t.id).includes(t.id) &&
            !this.selectedTableIds.includes(t.id) &&
            t.minimum_guests > this.number_of_guests - this.reservedGuests,
        )
      } else if (this.enforceTableMinimumEnabled && this.reservedGuests === this.number_of_guests) {
        return this.tables.filter(
          t =>
            !this.disabledTables.map(t => t.id).includes(t.id) &&
            !this.selectedTableIds.includes(t.id),
        )
      } else {
        return []
      }
    },
    availableTables(): Array<PerformanceTableRead & { isSelected: boolean }> {
      const tables = this.tables
        .filter(
          table =>
            !this.disabledTables.includes(table) &&
            !this.tablesNotFulfillTableMinimum.includes(table),
        )
        .map(table => {
          let canSeatAllGuestsOrder: number
          if (
            table.minimum_guests === this.number_of_guests &&
            table.maximum_guests === this.number_of_guests
          ) {
            canSeatAllGuestsOrder = 1
          } else if (
            table.minimum_guests < this.number_of_guests &&
            table.maximum_guests === this.number_of_guests
          ) {
            canSeatAllGuestsOrder = 2
          } else if (
            table.minimum_guests < this.number_of_guests &&
            table.maximum_guests > this.number_of_guests
          ) {
            canSeatAllGuestsOrder = 3
          } else {
            canSeatAllGuestsOrder = 4
          }
          return {
            ...table,
            isSelected: this.selectedTableIds.includes(table.id),
            order: canSeatAllGuestsOrder,
            price_per_person: +table.price_per_person,
          }
        })
      if (this.reservation) tables.push(...this.reservation.tables)
      return orderBy(tables, ['order', 'price_per_person'], ['asc', 'asc'])
    },
    selectedTables(): PerformanceTableRead[] {
      return this.tables.filter(table => this.selectedTableIds.includes(table.id))
    },
    disabledTables(): PerformanceTableRead[] {
      let standingRoomFilter = (t: PerformanceTableRead) => !t
      if (this.selectedTables.some(t => !t.standing_room)) {
        // if a user has selected a seated table, disable all standing room tables
        standingRoomFilter = t => t.standing_room
      } else if (this.selectedTables.some(t => t.standing_room)) {
        // if a user has selected a standing room table, disable all seated tables
        standingRoomFilter = t => !t.standing_room
      }
      const tables = this.tables.filter(
        table =>
          !table.available ||
          standingRoomFilter(table) ||
          this.number_of_guests < table.minimum_guests || // party too small for the table
          (table.standing_room && this.number_of_guests > table.available_guests), // standing_rooms need to accommodate the entire party
      )

      if (!this.reservation) return tables
      // Remove tables that are already reserved
      return tables.filter(table => !this.reservation?.tables.includes(table.id))
    },
    reservedGuests(): number {
      const guests = sum(this.seatedTables.map(table => table.guests))
      return Math.min(guests, this.number_of_guests)
    },
    seatedTables(): EnrichedTable[] {
      return tablesForSeatedParty(this.performance, this.selectedTableIds, this.number_of_guests)
    },
  },
})
</script>
